import { useState, useEffect } from 'react'
import { X, UserPlus, Trash2, Building2, ChevronDown, ChevronRight, AlertCircle, Check } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import {
  getPlanDisplayName,
  getSubscriptionStatusDisplayName,
  getPlanBadgeColor,
  getSubscriptionStatusBadgeColor,
} from '@/utils/permissions'

interface AgencyManagementModalProps {
  isOpen: boolean
  onClose: () => void
}

interface Agency {
  id: string
  name: string
  project_limit: number | null
  is_active: boolean
  plan_type: 'free' | 'pro' | 'agency'
  subscription_status: 'active' | 'expired' | 'cancelled' | 'trialing'
  subscription_start_date: string
  subscription_end_date: string | null
}

interface AgencyAdmin {
  id: string
  email: string
  name: string
  agency_id: string
}

export const AgencyManagementModal = ({ isOpen, onClose }: AgencyManagementModalProps) => {
  const [agencies, setAgencies] = useState<Agency[]>([])
  const [agencyAdmins, setAgencyAdmins] = useState<Record<string, AgencyAdmin[]>>({})
  const [expandedAgency, setExpandedAgency] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [addingAdmin, setAddingAdmin] = useState<string | null>(null)
  const [newAdminEmail, setNewAdminEmail] = useState('')
  const [newAdminName, setNewAdminName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      fetchAgencies()
    }
  }, [isOpen])

  const fetchAgencies = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true })

      if (error) throw error
      setAgencies(data || [])

      // Fetch admins for all agencies
      const { data: adminsData, error: adminsError } = await supabase
        .from('users')
        .select('id, email, name, agency_id')
        .eq('role', 'admin')
        .not('agency_id', 'is', null)

      if (adminsError) throw adminsError

      // Group admins by agency
      const adminsByAgency: Record<string, AgencyAdmin[]> = {}
      adminsData?.forEach((admin) => {
        if (!adminsByAgency[admin.agency_id]) {
          adminsByAgency[admin.agency_id] = []
        }
        adminsByAgency[admin.agency_id].push(admin)
      })

      setAgencyAdmins(adminsByAgency)
    } catch (err) {
      console.error('Error fetching agencies:', err)
      setError('Could not load agencies')
    } finally {
      setLoading(false)
    }
  }

  const handleAddAdmin = async (agencyId: string, e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!newAdminEmail.trim() || !newAdminName.trim()) {
      setError('Please fill in all fields')
      return
    }

    try {
      // Call Edge Function to create admin
      const response = await supabase.functions.invoke('create-admin', {
        body: {
          email: newAdminEmail.toLowerCase().trim(),
          name: newAdminName.trim(),
          agencyId: agencyId,
        },
      })

      const { data, error: funcError } = response

      // Parse error from Edge Function response
      if (funcError && response.response) {
        const errorBody = await response.response.json()
        if (errorBody.error) {
          throw new Error(errorBody.error)
        }
      }

      if (data) {
        if (data.success === false && data.error) {
          throw new Error(data.error)
        }
        if (!data.success) {
          throw new Error('Unexpected response from server')
        }
      } else if (funcError) {
        throw new Error(`Error: ${funcError.message}`)
      } else {
        throw new Error('No response received')
      }

      setSuccess(data.message || `Admin added to agency`)
      setNewAdminEmail('')
      setNewAdminName('')
      setAddingAdmin(null)
      fetchAgencies()
    } catch (err: any) {
      console.error('Error adding admin:', err)
      setError(err.message || 'Could not add admin')
    }
  }

  const handleRemoveAdmin = async (adminId: string, adminEmail: string) => {
    if (!confirm(`Are you sure you want to remove ${adminEmail} as admin?`)) {
      return
    }

    try {
      // Remove admin by setting their agency_id to null and role to client
      const { error } = await supabase
        .from('users')
        .update({ agency_id: null, role: 'client' })
        .eq('id', adminId)

      if (error) throw error

      setSuccess(`${adminEmail} removed as admin`)
      fetchAgencies()
    } catch (err) {
      console.error('Error removing admin:', err)
      setError('Could not remove admin')
    }
  }

  const toggleAgency = (agencyId: string) => {
    setExpandedAgency(expandedAgency === agencyId ? null : agencyId)
    setAddingAdmin(null)
    setNewAdminEmail('')
    setNewAdminName('')
    setError(null)
    setSuccess(null)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-black flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              Agency Management
            </h3>
            <p className="text-sm text-wenge-600 mt-1">Manage agency admins</p>
          </div>
          <button
            onClick={onClose}
            className="text-wenge-600 hover:text-wenge-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-sm text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2 text-sm text-green-700">
              <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-framed-yellow"></div>
            </div>
          ) : agencies.length === 0 ? (
            <div className="text-center py-12 text-wenge-600">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No agencies found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {agencies.map((agency) => {
                const admins = agencyAdmins[agency.id] || []
                const isExpanded = expandedAgency === agency.id
                const isAddingToThisAgency = addingAdmin === agency.id

                return (
                  <div
                    key={agency.id}
                    className="border border-framed-neutral-300 rounded-lg overflow-hidden"
                  >
                    {/* Agency Header */}
                    <button
                      onClick={() => toggleAgency(agency.id)}
                      className="w-full p-4 bg-white hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-wenge-600" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-wenge-600" />
                        )}
                        <Building2 className="w-5 h-5 text-framed-yellow" />
                        <div className="text-left flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-black">{agency.name}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded border font-medium ${getPlanBadgeColor(agency.plan_type)}`}>
                              {getPlanDisplayName(agency.plan_type)}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded border font-medium ${getSubscriptionStatusBadgeColor(agency.subscription_status)}`}>
                              {getSubscriptionStatusDisplayName(agency.subscription_status)}
                            </span>
                          </div>
                          <p className="text-sm text-wenge-600">
                            {admins.length} admin{admins.length !== 1 ? 's' : ''} · Limit: {agency.project_limit ?? '∞'} project{agency.project_limit !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    </button>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="p-4 bg-white border-t border-framed-neutral-300">
                        {/* Add Admin Form */}
                        {!isAddingToThisAgency ? (
                          <button
                            onClick={() => setAddingAdmin(agency.id)}
                            className="w-full p-3 mb-4 border-2 border-dashed border-framed-neutral-300 rounded-lg text-sm text-wenge-600 hover:border-framed-yellow hover:text-framed-yellow transition-colors flex items-center justify-center gap-2"
                          >
                            <UserPlus className="w-4 h-4" />
                            Add Admin
                          </button>
                        ) : (
                          <form
                            onSubmit={(e) => handleAddAdmin(agency.id, e)}
                            className="mb-4 p-4 bg-white rounded-lg border-2 border-framed-yellow"
                          >
                            <h5 className="font-semibold text-black mb-3 flex items-center gap-2">
                              <UserPlus className="w-4 h-4" />
                              New Admin
                            </h5>
                            <div className="space-y-3">
                              <div>
                                <label className="label text-xs">Name</label>
                                <input
                                  type="text"
                                  value={newAdminName}
                                  onChange={(e) => setNewAdminName(e.target.value)}
                                  placeholder="John Doe"
                                  required
                                  className="input w-full text-sm h-10"
                                  autoFocus
                                />
                              </div>
                              <div>
                                <label className="label text-xs">Email address</label>
                                <input
                                  type="email"
                                  value={newAdminEmail}
                                  onChange={(e) => setNewAdminEmail(e.target.value)}
                                  placeholder="admin@example.com"
                                  required
                                  className="input w-full text-sm h-10"
                                />
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAddingAdmin(null)
                                    setNewAdminEmail('')
                                    setNewAdminName('')
                                  }}
                                  className="flex-1 px-3 py-2 text-sm border border-framed-neutral-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="submit"
                                  disabled={!newAdminEmail || !newAdminName}
                                  className="flex-1 px-3 py-2 text-sm bg-framed-yellow text-white rounded-lg hover:bg-framed-yellow/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          </form>
                        )}

                        {/* Admins List */}
                        {admins.length === 0 ? (
                          <div className="text-center py-4 text-sm text-wenge-600">
                            No admins added yet
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <h5 className="text-xs font-medium text-wenge-600 uppercase tracking-wide mb-2">
                              Admins ({admins.length})
                            </h5>
                            {admins.map((admin) => (
                              <div
                                key={admin.id}
                                className="flex items-center justify-between p-3 bg-white rounded-lg border border-framed-neutral-300"
                              >
                                <div>
                                  <p className="font-medium text-black text-sm">
                                    {admin.name}
                                  </p>
                                  <p className="text-xs text-wenge-600">{admin.email}</p>
                                </div>
                                <button
                                  onClick={() => handleRemoveAdmin(admin.id, admin.email)}
                                  className="p-2 text-wenge-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Remove admin"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t">
          <button onClick={onClose} className="btn btn-secondary w-full">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
